/**
 * Item containers.
 *
 * One model covers the inventory, the bank, and worn equipment, because the rules
 * that differ between them are size and what may go in a slot, not how a slot works.
 *
 * The server owns every container. The client keeps its own copy purely so the
 * interface has something to draw between updates, and never acts on it: a slot the
 * client believes is empty is still checked on the server before anything moves.
 */

import { INVENTORY_SIZE } from './constants';

/** Bank slots. Generous, because running out is a support ticket, not a challenge. */
export const BANK_SIZE = 400;

/**
 * Equipment slots, in the order they are stored and drawn.
 *
 * The index is the slot number on the wire, so entries are appended, never
 * reordered. It matches the slot names in the content schema.
 */
export const EQUIPMENT_SLOT_NAMES = [
  'head',
  'cape',
  'neck',
  'weapon',
  'body',
  'shield',
  'legs',
  'hands',
  'feet',
  'ring',
  'ammunition',
] as const;

export type EquipmentSlotName = (typeof EQUIPMENT_SLOT_NAMES)[number];

export function equipmentSlotIndex(name: EquipmentSlotName): number {
  return EQUIPMENT_SLOT_NAMES.indexOf(name);
}

/** Which container an item is in. Values are on the wire, so they are permanent. */
export const ContainerId = {
  Inventory: 0,
  Bank: 1,
  Equipment: 2,
  Toolbelt: 3,
  /**
   * What this player has put up in a trade, and what the other one has.
   *
   * Two containers rather than one with a side, because everything that draws a
   * container already works a container at a time, and because the offer coming
   * the other way is read only: it arrives, it is looked at, and nothing here
   * ever writes to it.
   *
   * Neither is saved. A trade that is interrupted by anything at all is a trade
   * that never happened, so there is nothing to write down.
   */
  Offer: 4,
  TheirOffer: 5,
} as const;

export type ContainerIdValue = (typeof ContainerId)[keyof typeof ContainerId];

/** Names as stored in the database, indexed by container id. */
export const CONTAINER_NAMES: Readonly<Record<number, string>> = {
  [ContainerId.Inventory]: 'inventory',
  [ContainerId.Bank]: 'bank',
  [ContainerId.Equipment]: 'equipment',
  [ContainerId.Toolbelt]: 'toolbelt',
  // Not saved, and named only so that anything walking the table finds a word
  // rather than nothing.
  [ContainerId.Offer]: 'offer',
  [ContainerId.TheirOffer]: 'their offer',
};

/**
 * The belt: one slot for each job a tool does.
 *
 * A hatchet is a hatchet whatever it is made of, so a better one replaces the
 * one in that slot rather than taking a second slot beside it. Fishing has four
 * slots because it has four jobs: a net does not catch what a harpoon catches.
 *
 * Which slot a tool belongs in is read off its tags, which is where what a thing
 * is for is already written down.
 */
export const TOOLBELT_SLOTS: readonly { label: string; tag: string }[] = [
  { label: 'Hatchet', tag: 'tool_woodcutting' },
  { label: 'Pickaxe', tag: 'tool_mining' },
  { label: 'Tinderbox', tag: 'tool_firemaking' },
  { label: 'Hammer', tag: 'tool_smithing' },
  { label: 'Net', tag: 'tool_fishing_net' },
  { label: 'Rod', tag: 'tool_fishing_rod' },
  { label: 'Cage', tag: 'tool_fishing_cage' },
  { label: 'Harpoon', tag: 'tool_fishing_harpoon' },
];

/** Which slot of the belt a thing belongs in, or nothing when it is not a tool. */
export function toolbeltSlotFor(tags: readonly string[]): number | null {
  const at = TOOLBELT_SLOTS.findIndex((slot) => tags.includes(slot.tag));
  return at >= 0 ? at : null;
}

export function containerSize(container: ContainerIdValue): number {
  switch (container) {
    case ContainerId.Inventory:
      return INVENTORY_SIZE;
    case ContainerId.Bank:
      return BANK_SIZE;
    case ContainerId.Equipment:
      return EQUIPMENT_SLOT_NAMES.length;
    case ContainerId.Toolbelt:
      return TOOLBELT_SLOTS.length;
    case ContainerId.Offer:
    case ContainerId.TheirOffer:
      // A bag holds twenty eight and nobody can offer more than they carry.
      return INVENTORY_SIZE;
  }
}

/**
 * The most of one item a single slot can hold.
 *
 * Two billion and change, the point where the count stops fitting in a signed 32 bit
 * integer. Anything above it would have to be stored and sent as something wider, so
 * the cap is here rather than in the database.
 */
export const MAX_STACK = 2_147_483_647;

export interface ItemStack {
  id: number;
  quantity: number;
}

/** What a container needs to know about an item. Supplied by the content registry. */
export interface ItemRules {
  /** Whether many of this item share one slot. */
  stackable(itemId: number): boolean;
  /** Whether the item exists at all. An unknown id is never accepted into a slot. */
  exists(itemId: number): boolean;
}

/** Slots a change touched, so an update can carry the difference rather than the lot. */
export type SlotChanges = Set<number>;

export class Container {
  readonly slots: (ItemStack | null)[];

  /**
   * Whether everything stacks here regardless of what the item says.
   *
   * True for a bank, where a hundred hatchets are a number rather than a hundred
   * slots. It is a property of the container rather than the item, which is why it
   * lives here and not in the item definition.
   */
  private readonly alwaysStack: boolean;

  constructor(
    readonly id: ContainerIdValue,
    private readonly rules: ItemRules,
    size = containerSize(id),
  ) {
    this.slots = new Array<ItemStack | null>(size).fill(null);
    this.alwaysStack = id === ContainerId.Bank;
  }

  /** Whether an item shares one slot in this container. */
  stacks(itemId: number): boolean {
    return this.alwaysStack || this.rules.stackable(itemId);
  }

  get size(): number {
    return this.slots.length;
  }

  get(slot: number): ItemStack | null {
    return this.slots[slot] ?? null;
  }

  set(slot: number, stack: ItemStack | null): void {
    if (slot < 0 || slot >= this.slots.length) return;
    this.slots[slot] = stack && stack.quantity > 0 ? stack : null;
  }

  isEmpty(): boolean {
    return this.slots.every((slot) => slot === null);
  }

  freeSlots(): number {
    let free = 0;
    for (const slot of this.slots) if (slot === null) free++;
    return free;
  }

  /** Total held of one item, across every slot it occupies. */
  count(itemId: number): number {
    let total = 0;
    for (const slot of this.slots) {
      if (slot?.id === itemId) total += slot.quantity;
    }
    return total;
  }

  firstSlotOf(itemId: number): number {
    return this.slots.findIndex((slot) => slot?.id === itemId);
  }

  /**
   * Whether an amount would fit, without moving anything.
   *
   * Asked before a gathering action produces an item, so a full inventory stops the
   * action rather than quietly destroying what it made.
   */
  hasRoomFor(itemId: number, quantity = 1): boolean {
    if (quantity <= 0) return true;
    if (this.stacks(itemId)) {
      const existing = this.firstSlotOf(itemId);
      if (existing >= 0) {
        const stack = this.slots[existing];
        return stack ? MAX_STACK - stack.quantity >= quantity : true;
      }
      return this.freeSlots() > 0;
    }
    return this.freeSlots() >= quantity;
  }

  /**
   * Adds an amount, filling an existing stack first and then empty slots.
   *
   * Returns how much actually went in, which is less than asked for when the
   * container filled up. Callers that must not lose the remainder check the return
   * value; callers that can drop it on the floor have somewhere else to put it.
   */
  add(itemId: number, quantity = 1, changed?: SlotChanges): number {
    if (quantity <= 0 || !this.rules.exists(itemId)) return 0;

    let remaining = quantity;

    if (this.stacks(itemId)) {
      const existing = this.firstSlotOf(itemId);
      if (existing >= 0) {
        const stack = this.slots[existing];
        if (stack) {
          const room = MAX_STACK - stack.quantity;
          const moved = Math.min(room, remaining);
          if (moved > 0) {
            stack.quantity += moved;
            remaining -= moved;
            changed?.add(existing);
          }
          return quantity - remaining;
        }
      }

      const free = this.slots.indexOf(null);
      if (free < 0) return 0;
      this.slots[free] = { id: itemId, quantity: Math.min(remaining, MAX_STACK) };
      changed?.add(free);
      return Math.min(remaining, MAX_STACK);
    }

    // One slot each. A partial fill is deliberate: taking three of five logs is
    // better than refusing all five because two would not fit.
    for (let slot = 0; slot < this.slots.length && remaining > 0; slot++) {
      if (this.slots[slot] !== null) continue;
      this.slots[slot] = { id: itemId, quantity: 1 };
      changed?.add(slot);
      remaining--;
    }

    return quantity - remaining;
  }

  /** Removes from one slot. Returns how much was taken. */
  removeAt(slot: number, quantity = 1, changed?: SlotChanges): number {
    const stack = this.slots[slot];
    if (!stack || quantity <= 0) return 0;

    const taken = Math.min(stack.quantity, quantity);
    stack.quantity -= taken;
    if (stack.quantity <= 0) this.slots[slot] = null;
    changed?.add(slot);
    return taken;
  }

  /** Removes an amount of an item from wherever it happens to be. */
  remove(itemId: number, quantity = 1, changed?: SlotChanges): number {
    let remaining = quantity;

    for (let slot = 0; slot < this.slots.length && remaining > 0; slot++) {
      if (this.slots[slot]?.id !== itemId) continue;
      remaining -= this.removeAt(slot, remaining, changed);
    }

    return quantity - remaining;
  }

  /**
   * Swaps two slots, which is what dragging an item does.
   *
   * Deliberately a swap rather than an insert with everything shifting along. An
   * insert moves items the player did not touch, and a dropped drag would then be a
   * mess to undo.
   */
  swap(from: number, to: number, changed?: SlotChanges): boolean {
    if (from === to) return false;
    if (from < 0 || to < 0 || from >= this.slots.length || to >= this.slots.length) return false;

    const source = this.slots[from] ?? null;
    const destination = this.slots[to] ?? null;
    if (source === null && destination === null) return false;

    this.slots[from] = destination;
    this.slots[to] = source;
    changed?.add(from);
    changed?.add(to);
    return true;
  }

  /** Empties every slot. Used when loading a fresh set from the database. */
  clear(): void {
    this.slots.fill(null);
  }

  /** Non-empty slots, for persistence and for a full update. */
  entries(): { slot: number; stack: ItemStack }[] {
    const result: { slot: number; stack: ItemStack }[] = [];
    for (let slot = 0; slot < this.slots.length; slot++) {
      const stack = this.slots[slot];
      if (stack) result.push({ slot, stack: { ...stack } });
    }
    return result;
  }
}
